import { useCallback, useEffect, useMemo, useRef } from "react";

/**
 * Single-slot setTimeout. `schedule(cb, ms)` cancels any previously pending
 * callback, and `clear()` cancels without scheduling a new one. The pending
 * callback is cleared on unmount. Avoids the ref+cleanup boilerplate that
 * otherwise crops up wherever a component needs to debounce, delay, or hold a
 * transient state for N ms.
 */
export function useTimeoutRef() {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clear = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const schedule = useCallback(
    (callback: () => void, ms: number) => {
      clear();
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        callback();
      }, ms);
    },
    [clear]
  );

  useEffect(() => clear, [clear]);

  return useMemo(() => ({ schedule, clear }), [schedule, clear]);
}
